import { inject, Injectable } from '@angular/core';
import { AppConfig } from '../../app/app.config';
import { GenderFormErrorMessages } from '../../../e2e/src/shared/constants';

/** Ages at or above this are rejected as typos rather than real ages. */
const MAX_AGE = 100;

/** The backend stores at most a three-word patient name. */
const MAX_NAME_WORDS = 3;

// eslint-disable-next-line max-len
const NAME_DISALLOWED_REGEX: RegExp = /[0-9$&+.,:;=?@#|"'<>^*()%!_{}\\/]|[\u{1F300}-\u{1FAFF}\u{1F600}-\u{1F64F}\u{2700}-\u{27BF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}]/u;

/**
 * Name and age validation shared by the single-page `onboarding-gender` form and the split
 * gender/name/age pages, so the two cannot drift apart.
 */
@Injectable({
  providedIn: 'root',
})
export class OnboardingProfileValidationService {
  private readonly appConfig: AppConfig = inject(AppConfig);

  /** Returns a `GenderFormErrorMessages` value, or `''` when valid. Templates match on it for i18n. */
  validateName(name: string): string {
    if (NAME_DISALLOWED_REGEX.test(name)) return GenderFormErrorMessages.numberInName;
    if (!name?.split(' ')[0]) return GenderFormErrorMessages.emptyName;
    if (name.split(' ').length > MAX_NAME_WORDS) return GenderFormErrorMessages.maxNameLength;
    return '';
  }

  validateAge(age: string): string {
    if (!age) return GenderFormErrorMessages.enterAge;
    const parsedAge = Number(age);
    if (parsedAge <= this.appConfig.Shared.User.Age.MIN_AGE) return GenderFormErrorMessages.underAged;
    if (parsedAge >= MAX_AGE) return GenderFormErrorMessages.invalidAge;
    return '';
  }

  /** Strips everything but digits, so the numeric keypad cannot inject separators. */
  sanitizeAge(value: string): string {
    return value.replace(/[^0-9]/g, '');
  }
}
